/**
 * Primitivas de UI partilhadas pelos ecrãs de staff e gestão.
 *
 * Vieram do projecto Bamidi-main (PageTitle, Metric, Badge, Empty, Field),
 * onde eram o que dava coerência a oito ecrãs. Aqui estão tipadas e com o
 * estado do pedido a mapear para cor em vez de ser decidido à mão em cada
 * ecrã — era fácil um ecrã pintar "Cancelado" de verde.
 */

import type { ReactNode } from 'react';

import type { OrderState } from '../../types/domain';

/* ------------------------------------------------------------- cabeçalho -- */

interface PageTitleProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  status?: string;
  actions?: ReactNode;
}

export const PageTitle = ({
  eyebrow,
  title,
  subtitle,
  status,
  actions,
}: PageTitleProps) => (
  <header className="page-title">
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'flex-end',
        justifyContent: 'space-between',
      }}
    >
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="btn-row">{actions}</div>}
    </div>
    {status && <p className="page-status">{status}</p>}
  </header>
);

/* --------------------------------------------------------------- métrica -- */

export const Metric = ({
  label,
  value,
  alert = false,
}: {
  label: string;
  value: ReactNode;
  alert?: boolean;
}) => (
  <div className={`metric ${alert ? 'alert' : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

/* ---------------------------------------------------------------- badge -- */

/**
 * Cor e texto de um estado, num sítio só.
 *
 * Cada ecrã escolhia a sua, e "Cancelado" chegou a aparecer com o mesmo tom
 * de "Entregue".
 */
const ESTADOS: Record<OrderState, { texto: string; tom: string }> = {
  W: { texto: 'Aguarda funcionário', tom: 'accent' },
  P: { texto: 'Pendente', tom: 'info' },
  Z: { texto: 'Em preparação', tom: 'warn' },
  E: { texto: 'Entregue', tom: 'ok' },
  C: { texto: 'Cancelado', tom: 'danger' },
};

export const OrderBadge = ({ estado }: { estado: OrderState }) => {
  const info = ESTADOS[estado];
  return <span className={`badge ${info?.tom ?? ''}`}>{info?.texto ?? estado}</span>;
};

export const Badge = ({
  children,
  tom = '',
}: {
  children: ReactNode;
  tom?: 'ok' | 'warn' | 'danger' | 'info' | 'accent' | '';
}) => <span className={`badge ${tom}`}>{children}</span>;

/* ---------------------------------------------------------------- vazio -- */

export const Empty = ({ title, text }: { title: string; text?: string }) => (
  <div className="empty">
    <strong>{title}</strong>
    {text && <span>{text}</span>}
  </div>
);

/* ------------------------------------------------------------ formulário -- */

interface FieldProps {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  min?: number;
  required?: boolean;
  options?: { value: string; label: string }[];
}

export const Field = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  min,
  required,
  options,
}: FieldProps) => (
  <label className="field">
    <span>{label}</span>
    {options ? (
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        value={value}
        min={min}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    )}
  </label>
);

export const Check = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) => (
  <label className="check-row">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    {label}
  </label>
);

/* -------------------------------------------------------------- feedback -- */

export const Feedback = ({
  message,
  tom = 'error',
}: {
  message: string | null;
  tom?: 'error' | 'ok';
}) => (message ? <p role={tom === 'error' ? 'alert' : 'status'} className={`feedback ${tom}`}>{message}</p> : null);

export const Card = ({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) => (
  <section className="card">
    {title && <h2 className="card-title">{title}</h2>}
    {children}
  </section>
);
